import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWatchlist } from '../hooks/useWatchlist';
import { searchStocks, fetchStockData } from '../lib/scoring';
import { StockCard } from '../components/StockCard';
import { PremiumPrompt } from '../components/PremiumPrompt';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function useSearchResults() {
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const timerRef = React.useRef(null);

  const search = useCallback((query) => {
    clearTimeout(timerRef.current);
    if (query.length < 1) {
      setItems([]);
      setSearchError(null);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      setItems([]);
      try {
        const results = await searchStocks(query);
        setItems(
          results.map((s) => ({
            symbol: s.symbol,
            stock: s,
            loading: false,
            error: null,
          }))
        );
      } catch (err) {
        setSearchError(err.message || 'Error al buscar. Intenta de nuevo más tarde.');
      } finally {
        setSearching(false);
      }
    }, 700);
  }, []);

  return { items, searching, searchError, search };
}

export function Dashboard() {
  const [query, setQuery] = useState('');
  const { isPremium } = useAuth();
  const { watchlistRows } = useWatchlist();
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [watchlistEnriching, setWatchlistEnriching] = useState(false);
  const { items, searching, searchError, search } = useSearchResults();

  useEffect(() => {
    if (!watchlistRows.length) {
      setWatchlistStocks([]);
      setWatchlistEnriching(false);
      return;
    }
    let cancelled = false;
    setWatchlistEnriching(true);
    (async () => {
      const results = await Promise.all(
        watchlistRows.map(async (row) => {
          try {
            return await fetchStockData(row.ticker);
          } catch {
            return {
              noData: true,
              symbol: row.ticker,
              name: row.ticker,
              message: 'No se pudieron cargar los datos.',
              status: 'Sin datos',
            };
          }
        })
      );
      if (!cancelled) {
        setWatchlistStocks(results);
        setWatchlistEnriching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [watchlistRows]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    search(val);
  };

  return (
    <div className="space-y-8">
      {!isPremium && <PremiumPrompt />}

      <section>
        <h2 className="text-2xl font-bold mb-4">Buscador de Activos</h2>

        <div className="relative max-w-2xl mb-8">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {searching ? (
              <SafeIcon icon={FiIcons.FiLoader} className="text-brand-red animate-spin" />
            ) : (
              <SafeIcon icon={FiIcons.FiSearch} className="text-gray-400" />
            )}
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-4 border border-gray-200 rounded-xl bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-red focus:border-brand-red sm:text-sm shadow-sm transition-shadow"
            placeholder="Buscar por símbolo (ej. AAPL) o nombre de empresa..."
            value={query}
            onChange={handleChange}
          />
        </div>

        {searchError && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="mt-0.5 flex-shrink-0" />
            <span>{searchError}</span>
          </div>
        )}

        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <StockCard
                key={item.symbol}
                stock={item.stock}
                loading={item.loading}
                error={item.error}
              />
            ))}
          </div>
        )}

        {!searching && !searchError && items.length === 0 && query.length > 1 && (
          <p className="text-gray-500 py-8">No se encontraron resultados para "{query}"</p>
        )}
      </section>

      <section className="pt-8 border-t border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiList} />
            Mi Lista de Seguimiento
          </h2>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {watchlistRows.length} {isPremium ? '' : '/ 10'} guardados
          </span>
        </div>

        {watchlistEnriching && watchlistRows.length > 0 ? (
          <p className="text-gray-500 py-6">Cargando lista…</p>
        ) : watchlistStocks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {watchlistStocks.map((stock) => (
              <StockCard
                key={stock.symbol}
                stock={
                  stock.noData
                    ? {
                        symbol: stock.symbol,
                        name: stock.name,
                        price: null,
                        score: null,
                        mos: null,
                        status: 'Sin datos',
                        noData: true,
                        errorMessage: stock.message,
                      }
                    : stock
                }
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
            <SafeIcon icon={FiIcons.FiHeart} className="mx-auto text-4xl text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Lista vacía</h3>
            <p className="text-gray-500">
              Busca acciones arriba y guárdalas usando el ícono de corazón.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
